/**
 * ConfirmDialog - Reusable confirmation dialog for destructive/important actions.
 * Drop-in replacement for window.confirm() with consistent styling.
 *
 * Usage pattern (imperative):
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: "Borrar?", variant: "danger" });
 *   if (ok) doDelete();
 */
import { createContext, useCallback, useContext, useState } from "react";
import { AlertTriangle, HelpCircle, Info } from "lucide-react";
import { Modal, ModalFooter } from "./Modal";
import Button from "./Button";

type Variant = "danger" | "warning" | "info";

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: Variant;
}

interface InternalState extends ConfirmOptions {
  isOpen: boolean;
  resolve?: (value: boolean) => void;
}

const ConfirmContext = createContext<
  (opts: ConfirmOptions) => Promise<boolean>
>(() => Promise.resolve(false));

const iconMap: Record<Variant, React.ReactNode> = {
  danger: <AlertTriangle className="w-6 h-6 text-red-600" />,
  warning: <AlertTriangle className="w-6 h-6 text-amber-600" />,
  info: <Info className="w-6 h-6 text-blue-600" />,
};

const iconBgMap: Record<Variant, string> = {
  danger: "bg-red-100",
  warning: "bg-amber-100",
  info: "bg-blue-100",
};

export function ConfirmDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<InternalState>({
    isOpen: false,
    title: "",
  });

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setState({
        ...opts,
        isOpen: true,
        resolve,
      });
    });
  }, []);

  const handleClose = (result: boolean) => {
    state.resolve?.(result);
    setState((s) => ({ ...s, isOpen: false }));
  };

  const variant = state.variant ?? "warning";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        isOpen={state.isOpen}
        onClose={() => handleClose(false)}
        size="sm"
        showCloseButton={false}
      >
        <div className="flex gap-4">
          <div
            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${iconBgMap[variant]}`}
          >
            {iconMap[variant]}
          </div>
          <div className="flex-1 pt-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {state.title}
            </h3>
            {state.description && (
              <p className="text-sm text-gray-600 whitespace-pre-line">
                {state.description}
              </p>
            )}
          </div>
        </div>
        <ModalFooter>
          <Button variant="ghost" onClick={() => handleClose(false)}>
            {state.cancelText ?? "Cancelar"}
          </Button>
          <Button
            variant={variant === "danger" ? "danger" : "primary"}
            onClick={() => handleClose(true)}
          >
            {state.confirmText ?? "Confirmar"}
          </Button>
        </ModalFooter>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}

/** Fallback helper if used outside provider (rare). */
export { HelpCircle };
